import { Checkbox as CheckboxPrimitive } from 'radix-ui'
import type * as React from 'react'

import { cn } from '../../lib/utils'

/**
 * A 16px box with 3px corners and an `--input` boundary at the 3:1 floor.
 * Checked and indeterminate are an ink fill with a paper mark; the ink is the
 * selection accent (DESIGN.md §2 "Primary"), told apart from text by its
 * shape. Disabled is recoloured, never faded; a disabled checked box keeps
 * its tick on a `--border` fill so the state still reads.
 *
 * Indeterminate is not decorative: the data table's select-all header is in
 * it whenever some but not all rows are ticked, and showing that as unchecked
 * would misstate what a bulk action will touch.
 */
function Checkbox({ className, ...props }: React.ComponentProps<typeof CheckboxPrimitive.Root>) {
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      className={cn(
        'peer inline-flex size-4 shrink-0 items-center justify-center rounded-badge border border-input bg-input-background transition-ui',
        'enabled:hover:border-foreground',
        'focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring',
        'data-[state=checked]:border-brand data-[state=checked]:bg-brand',
        'data-[state=indeterminate]:border-brand data-[state=indeterminate]:bg-brand',
        'aria-invalid:border-danger',
        'disabled:cursor-not-allowed disabled:border-border disabled:bg-muted',
        'disabled:data-[state=checked]:bg-border disabled:data-[state=indeterminate]:bg-border',
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="flex animate-mark-in items-center justify-center text-brand-foreground in-disabled:text-muted-foreground"
      >
        {props.checked === 'indeterminate' ? (
          <span className="h-0.5 w-2 bg-current" />
        ) : (
          // Hand-drawn rather than an icon: at this size the icon set's stroke
          // and padding leave the tick visibly off-centre.
          <svg
            viewBox="0 0 10 10"
            className="size-2.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="square"
            aria-hidden="true"
          >
            <path d="M1.5 5.2 L4 7.5 L8.5 2.5" />
          </svg>
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
}

export { Checkbox }
